import React, {FC, useCallback, useEffect, useRef} from 'react';
import {animated, useSpring} from 'react-spring';
import {useGesture} from 'react-use-gesture';
import styled from 'styled-components';
import {COLORS} from '../../../constants/colors';
import {useOnDoubleClick} from '../../../hooks/useOnDoubleClick/useOnDoubleClick';
import {ProductImage, ProductImageProps} from '../ProductImage/ProductImage';

/**
 * Refer to the videos below to understand how the code behaves
 * @see https://www.youtube.com/watch?v=dBWhAGhw7wM
 * @see https://www.youtube.com/watch?v=y-IvlWmp7dU
 * @see https://www.youtube.com/watch?v=UVpO7yRlEfw
 */
export const ProductImageZoom: FC<ProductImageProps> = ({
  src,
  className,
  ...rest
}) => {
  const [style, setStyle] = useSpring(() => ({
    x: 0,
    y: 0,
    scale: 1,
    touchAction: 'auto',
    config: {
      mass: 1.3,
      tension: 500,
      friction: 20,
      clamp: true,
      velocity: 30,
    },
  }));
  const imageRef = useRef<HTMLImageElement | null>(null);
  const imageContainerRef = useRef<HTMLDivElement | null>(null);
  const dimensions = useRef<DOMRect | null>(null);

  // This workaround is needed to prevent page zoom in Safari iOS
  useEffect(() => {
    const listener: EventListener = e => e.preventDefault();

    document.addEventListener('gesturestart', listener);
    document.addEventListener('gesturechange', listener);

    return () => {
      document.removeEventListener('gesturestart', listener);
      document.removeEventListener('gesturechange', listener);
    };
  }, []);

  const readDimensions = useCallback(() => {
    dimensions.current =
      dimensions.current ?? imageRef.current!.getBoundingClientRect();

    return dimensions.current!;
  }, []);

  const maybeAdjustImage = useCallback(() => {
    const newStyle = {
      x: style.x.get(),
      y: style.y.get(),
      scale: style.scale.get(),
    };
    const imageBounds = imageRef.current!.getBoundingClientRect();
    const containerBounds = imageContainerRef.current!.getBoundingClientRect();
    const originalWidth = imageRef.current!.clientWidth;
    const widthOverhang = (imageBounds.width - originalWidth) / 2;
    const originalHeight = imageRef.current!.clientHeight;
    const heightOverhang = (imageBounds.height - originalHeight) / 2;

    if (imageBounds.left > containerBounds.left) {
      newStyle.x = widthOverhang;
    } else if (imageBounds.right < containerBounds.right) {
      newStyle.x = -(imageBounds.width - containerBounds.width) + widthOverhang;
    }

    if (imageBounds.top > containerBounds.top) {
      newStyle.y = heightOverhang;
    } else if (imageBounds.bottom < containerBounds.bottom) {
      newStyle.y =
        -(imageBounds.height - containerBounds.height) + heightOverhang;
    }

    setStyle.start(newStyle);
  }, [style, setStyle]);

  const toggleTouchAction = (scale: number) => {
    setStyle.set({
      touchAction: scale > 1 ? 'none' : 'auto',
    });
  };

  useGesture(
    {
      onDrag: ({pinching, cancel, canceled, movement: [dx, dy]}) => {
        if (canceled) return;
        if (pinching) return cancel();
        if (style.scale.get() === 1) return cancel();
        setStyle.set({x: dx, y: dy});
      },
      onPinch: ({memo, origin: [pinchOriginX, pinchOriginY], offset: [d]}) => {
        memo ??= {
          bounds: imageRef.current!.getBoundingClientRect(),
          style: {
            x: style.x.get(),
            y: style.y.get(),
            scale: style.scale.get(),
          },
        };

        const transformOriginX = memo.bounds.x + memo.bounds.width / 2;
        const transformOriginY = memo.bounds.y + memo.bounds.height / 2;

        const displacementX =
          (transformOriginX - pinchOriginX) / memo.style.scale;
        const displacementY =
          (transformOriginY - pinchOriginY) / memo.style.scale;

        // the higher the slower
        const smoothFactor = 95;

        const initialOffsetDistance = (memo.style.scale - 1) * smoothFactor;
        const movementDistance = d - initialOffsetDistance;

        const scale = 1 + d / smoothFactor;
        setStyle.start({
          scale,
          x: memo.style.x + (displacementX * movementDistance) / smoothFactor,
          y: memo.style.y + (displacementY * movementDistance) / smoothFactor,
        });
        toggleTouchAction(scale);

        return memo;
      },
      onDragEnd: maybeAdjustImage,
      onPinchEnd: maybeAdjustImage,
    },
    {
      domTarget: imageRef as EventTarget | React.RefObject<EventTarget>,
      eventOptions: {passive: false},
      drag: {
        initial: () => [style.x.get(), style.y.get()],
        threshold: 10,
        rubberband: true,
      },
      pinch: {
        // 190 = scale 3 times
        distanceBounds: {min: 0, max: 190},
      },
    },
  );

  useOnDoubleClick({
    onDoubleClick: (e: TouchEvent) => {
      if (style.scale.get() === 1) {
        const {width, height} = readDimensions();
        const touch = e.changedTouches;
        const offsetX = touch[0]?.clientX ?? 0;
        const offsetY = touch[0]?.clientY ?? 0;

        const tx = width / 2 - offsetX;
        const ty = height / 2 - offsetY;

        const scale = 2;

        setStyle.start({
          scale,
          x: tx || width / 2,
          y: ty || height / 2,
        });
        toggleTouchAction(scale);
      } else {
        const scale = 1;
        setStyle.start({scale, x: 0, y: 0});
        toggleTouchAction(scale);
      }
    },
    ref: imageRef,
    latency: 500,
  });

  return (
    <Wrapper ref={imageContainerRef} className={className}>
      <AnimatedProductImage {...rest} ref={imageRef} src={src} style={style} />
    </Wrapper>
  );
};

const Wrapper = styled.div`
  overflow: hidden;
  position: relative;
  background-color: ${COLORS.ProductImageBackground};
`;

const StyledProductImage = styled(ProductImage)`
  height: 100% !important;
  padding-top: 0 !important;
`;

const AnimatedProductImage = animated(StyledProductImage);
